import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertCircle, Check, ChevronDown, GripVertical, Loader2, Plus, Trash2, X } from 'lucide-react';
import UnitMappingService from '../services/unitMappingService';
import './menuorganization.css';

const API_URL = process.env.REACT_APP_API_URL;

/* ── Helpers ─────────────────────────────────────────────────── */

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  const businessId = UnitMappingService.getBusinessId();
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
    'X-Business-ID': businessId,
  };
};

/* ── Component ───────────────────────────────────────────────── */

const MenuOrganization = ({ isOpen = true, onClose, onComplete, gateMode = false }) => {
  // Data state
  const [subcats, setSubcats] = useState([]);       // MenuCategories rows
  const [items, setItems] = useState([]);            // MenuItems rows
  const [masters, setMasters] = useState([]);        // MasterMenuCategories rows (with subCategoryIds)

  // UI state
  const [view, setView] = useState('subcategories');
  const [search, setSearch] = useState('');
  const [searchMode, setSearchMode] = useState('item'); // 'item' | 'category'
  const [searchDropdownOpen, setSearchDropdownOpen] = useState(false);
  const searchDropdownRef = useRef(null);
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [dragSubCatId, setDragSubCatId] = useState(null);
  const [dragOverMasterId, setDragOverMasterId] = useState(null);
  const [dragItemId, setDragItemId] = useState(null);
  const [dragOverSubCatId, setDragOverSubCatId] = useState(null);

  // Async state
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);

  // Custom cuisine input
  const [showAddInput, setShowAddInput] = useState(false);
  const [newCuisineName, setNewCuisineName] = useState('');

  // Unassigned ingredients warning (Edge Case 2)
  const [unassignedIngredients, setUnassignedIngredients] = useState([]);
  const [showUnassignedWarning, setShowUnassignedWarning] = useState(false);

  /* ── Toast helper ────────────────────────────────────────── */
  const showToast = useCallback((msg, duration = 3000) => {
    setToast(msg);
    setTimeout(() => setToast(null), duration);
  }, []);

  /* ── Load data from DB ───────────────────────────────────── */
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const headers = getAuthHeaders();
      const businessId = UnitMappingService.getBusinessId();

      // Seed defaults first (safe — no-ops if already seeded)
      await fetch(`${API_URL}/master-categories/seed-defaults`, {
        method: 'POST',
        headers,
      });

      // Parallel fetch: master categories + all micro-categories (critical)
      const [mastersRes, categoriesRes] = await Promise.all([
        fetch(`${API_URL}/master-categories?businessId=${businessId}`, { headers }),
        fetch(`${API_URL}/menu/categories?businessId=${businessId}`, { headers }),
      ]);

      const mastersData = await mastersRes.json();
      const categoriesData = await categoriesRes.json();

      if (!mastersData.success || !categoriesData.success) {
        throw new Error('Failed to load categories from server');
      }

      // Map DB categories → subcats
      const allCategories = (categoriesData.data || []).map((cat) => ({
        id: cat.id,
        name: cat.name,
        masterCategoryId: cat.master_category_id || null,
      }));
      setSubcats(allCategories);

      // Map DB masters → masters with subCategoryIds
      const mastersList = (mastersData.data || []).map((m) => ({
        id: m.master_category_id,
        name: m.name,
        displayOrder: m.display_order,
        subCategoryIds: (m.microCategories || []).map((mc) => mc.category_id),
      }));
      setMasters(mastersList);

      // Fetch items separately (non-critical — may fail due to RBAC)
      try {
        const itemsRes = await fetch(`${API_URL}/menu/items?language=en&includeInactive=true`, { headers });
        console.log('[MenuOrg] items response status:', itemsRes.status);
        if (itemsRes.ok) {
          const itemsData = await itemsRes.json();
          console.log('[MenuOrg] items data:', itemsData.success, 'count:', itemsData.data?.length);
          if (itemsData.success && itemsData.data?.length > 0) {
            const itemsList = itemsData.data.map((item) => {
              const matchedCat = allCategories.find((c) => c.name === item.category);
              return {
                id: item.id,
                name: item.name,
                subCategoryId: matchedCat?.id || null,
                originalSubCategoryId: matchedCat?.id || null,
              };
            });
            setItems(itemsList);
            console.log('[MenuOrg] Loaded', itemsList.length, 'items');
          } else {
            console.warn('[MenuOrg] Items API returned empty or failed:', itemsData);
            setItems([]);
          }
        } else {
          console.warn('[MenuOrg] Items API returned', itemsRes.status, '- skipping items');
          setItems([]);
        }
      } catch (itemsErr) {
        console.warn('[MenuOrg] Items fetch failed (non-critical):', itemsErr.message);
        setItems([]);
      }
    } catch (err) {
      console.error('❌ MenuOrganization loadData error:', err);
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) loadData();
  }, [isOpen, loadData]);

  /* ── Derived data ────────────────────────────────────────── */
  const mappedSubCatIds = useMemo(
    () => new Set(masters.flatMap((m) => m.subCategoryIds)),
    [masters],
  );

  const getItemCount = (subCategoryId) =>
    items.filter((item) => item.subCategoryId === subCategoryId).length;

  const unmappedSubcats = subcats.filter(
    (sc) => !mappedSubCatIds.has(sc.id) && getItemCount(sc.id) > 0
  );
  
  const filteredItems = items.filter((item) => {
    const q = search.toLowerCase();
    if (!q) return true;
    if (searchMode === 'category') {
      const subCat = subcats.find(sc => sc.id === item.subCategoryId);
      return (subCat?.name || '').toLowerCase().includes(q);
    }
    return item.name.toLowerCase().includes(q);
  });
  
  const totalMapped = masters.reduce((c, m) => c + m.subCategoryIds.length, 0);
  const canFinalize = totalMapped > 0 && unmappedSubcats.length === 0;

  const getSubCategory = (subCategoryId) =>
    subcats.find((sc) => sc.id === subCategoryId);

  /* ── Local actions (not persisted until Finalize) ──────── */
  const reassignItem = (itemId, subCategoryId) => {
    setItems((prev) =>
      prev.map((item) =>
        item.id === itemId ? { ...item, subCategoryId } : item,
      ),
    );
    setSelectedItemId(null);
  };

  const handleDropOnMaster = (masterId) => {
    if (!dragSubCatId) return;

    setMasters((prev) =>
      prev.map((m) => {
        const without = m.subCategoryIds.filter((id) => id !== dragSubCatId);
        if (m.id === masterId) {
          return { ...m, subCategoryIds: [...without, dragSubCatId] };
        }
        return { ...m, subCategoryIds: without };
      }),
    );
    setDragSubCatId(null);
    setDragOverMasterId(null);
  };

  const removeFromMaster = (masterId, subCategoryId) => {
    setMasters((prev) =>
      prev.map((m) =>
        m.id === masterId
          ? { ...m, subCategoryIds: m.subCategoryIds.filter((id) => id !== subCategoryId) }
          : m,
      ),
    );
  };

  /* ── Add Custom Cuisine (persisted immediately) ────────── */
  const addCustomCuisine = async () => {
    const name = newCuisineName.trim();
    if (!name) return;

    try {
      const headers = getAuthHeaders();
      const res = await fetch(`${API_URL}/master-categories`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ name }),
      });
      const data = await res.json();

      if (data.success) {
        setMasters((prev) => [
          ...prev,
          {
            id: data.data.master_category_id,
            name: data.data.name,
            displayOrder: data.data.display_order,
            subCategoryIds: [],
          },
        ]);
        setNewCuisineName('');
        setShowAddInput(false);
      } else {
        showToast(`❌ ${data.error || 'Failed to create'}`);
      }
    } catch (err) {
      showToast('❌ Network error creating cuisine');
    }
  };

  const deleteMasterCuisine = async (masterId, name) => {
    try {
      const headers = getAuthHeaders();
      const res = await fetch(`${API_URL}/master-categories/${masterId}`, {
        method: 'DELETE',
        headers
      });
      const data = await res.json();
      if (data.success) {
        setMasters((prev) => prev.filter((m) => m.id !== masterId));
      } else {
        showToast(`❌ ${data.error || 'Failed to delete'}`);
      }
    } catch (err) {
      showToast('❌ Network error deleting cuisine');
    }
  };

  /* ── Finalize: persist all mappings to DB ──────────────── */
  const completeMapping = async () => {
    if (!canFinalize) return;

    setSaving(true);
    try {
      const headers = getAuthHeaders();

      // 1) For each master, merge its current subCategoryIds
      let allUnassigned = [];
      for (const master of masters) {
        if (master.subCategoryIds.length > 0) {
          const res = await fetch(`${API_URL}/master-categories/merge`, {
            method: 'PUT',
            headers,
            body: JSON.stringify({
              masterCategoryId: master.id,
              microCategoryIds: master.subCategoryIds,
            }),
          });
          const data = await res.json();
          if (!data.success) {
            showToast(`❌ Failed to save ${master.name}: ${data.error}`);
            setSaving(false);
            return;
          }
          // Collect unassigned ingredients from each merge response
          if (data.unassigned_ingredients?.length > 0) {
            allUnassigned = allUnassigned.concat(data.unassigned_ingredients);
          }
        }
      }

      // 2) Unassign any sub-categories that are in the unmapped pool
      //    (i.e. removed from all masters during this session)
      const allAssigned = new Set(masters.flatMap((m) => m.subCategoryIds));
      const unassigned = subcats
        .filter((sc) => !allAssigned.has(sc.id) && sc.masterCategoryId !== null)
        .map((sc) => sc.id);

      if (unassigned.length > 0) {
        await fetch(`${API_URL}/master-categories/merge`, {
          method: 'PUT',
          headers,
          body: JSON.stringify({
            masterCategoryId: null,
            microCategoryIds: unassigned,
          }),
        });
      }

      // 3) Reassign item categories
      const changedItems = items.filter(item => item.subCategoryId !== item.originalSubCategoryId);
      if (changedItems.length > 0) {
        const itemAssignments = changedItems.map(item => ({
          itemId: item.id,
          categoryId: item.subCategoryId
        }));
        
        const itemRes = await fetch(`${API_URL}/menu/items/reassign-categories`, {
          method: 'PUT',
          headers,
          body: JSON.stringify({ assignments: itemAssignments }),
        });
        
        const itemData = await itemRes.json();
        if (!itemData.success) {
          console.error('Failed to save item category reassignments:', itemData.error);
        }
      }

      // Edge Case 2: Show warning if unassigned ingredients detected
      // Deduplicate by item_id
      const uniqueUnassigned = [...new Map(allUnassigned.map(i => [i.item_id, i])).values()];
      if (uniqueUnassigned.length > 0) {
        setUnassignedIngredients(uniqueUnassigned);
        setShowUnassignedWarning(true);
        // Don't return — warn + allow proceed
      }

      showToast('✅ Mapping saved successfully!');
      if (!uniqueUnassigned.length) {
        if (onComplete) onComplete();
      }
    } catch (err) {
      showToast('❌ Failed to save mapping');
      console.error('Finalize error:', err);
    } finally {
      setSaving(false);
    }
  };

  // Auto-scroll body container during drag operations
  const handleDragOverBody = (event) => {
    if (!dragSubCatId && !dragItemId) return;
    const body = event.currentTarget;
    if (!body) return;
    const rect = body.getBoundingClientRect();
    const relativeY = event.clientY - rect.top;
    const threshold = 60;
    const maxScrollSpeed = 15;
    if (relativeY > rect.height - threshold) {
      const ratio = (relativeY - (rect.height - threshold)) / threshold;
      body.scrollTop += Math.ceil(ratio * maxScrollSpeed);
    } else if (relativeY < threshold) {
      const ratio = (threshold - relativeY) / threshold;
      body.scrollTop -= Math.ceil(ratio * maxScrollSpeed);
    }
  };

  const isDragging = dragSubCatId !== null || dragItemId !== null;

  if (!isOpen) return null;

  /* ── Render ────────────────────────────────────────────── */
  return (
    <div className="sp-modal-overlay mo-overlay">
      <div className={`mo-shell ${isDragging ? 'mo-dragging-active' : ''}`} role="dialog" aria-modal="true" aria-labelledby="mo-title">
        <div className="mo-header">
          <div>
            <h2 id="mo-title">{gateMode ? 'Step 1: Consolidate Categories' : 'Organize Menu Categories'}</h2>
            {gateMode && <p>Map all micro-categories before proceeding</p>}
          </div>
          {!gateMode && (
            <button type="button" className="mo-icon-button" onClick={onClose} aria-label="Close">
              <X size={20} strokeWidth={1.8} />
            </button>
          )}
        </div>

        {/* Gate enforcement banner */}
        {gateMode && unmappedSubcats.length > 0 && (
          <div className="mo-gate-banner">
            <AlertCircle size={16} />
            <span>Cannot finalize workspace: {unmappedSubcats.length} micro-categories require an assigned destination folder.</span>
          </div>
        )}

        {/* Unassigned Ingredients Warning (Edge Case 2) */}
        {showUnassignedWarning && (
          <div className="mo-unassigned-overlay">
            <div className="mo-unassigned-modal">
              <h3>⚠️ Unassigned Base Ingredients</h3>
              <p>{unassignedIngredients.length} raw ingredient(s) are not yet categorized in inventory. They will appear in the "Unassigned Base Items" section on your dashboard.</p>
              <div className="mo-unassigned-list">
                {unassignedIngredients.slice(0, 10).map(ing => (
                  <div key={ing.item_id} className="mo-unassigned-item">
                    <span>{ing.ingredient_name}</span>
                  </div>
                ))}
                {unassignedIngredients.length > 10 && (
                  <div className="mo-unassigned-more">+{unassignedIngredients.length - 10} more</div>
                )}
              </div>
              <div className="mo-unassigned-actions">
                <button onClick={() => { setShowUnassignedWarning(false); if (onComplete) onComplete(); }} className="mo-btn-primary">
                  Got it — Proceed
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Loading / Error states ─────────────────────── */}
        {loading ? (
          <div className="mo-body" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
            <Loader2 size={28} className="mo-spinner" strokeWidth={2} />
            <span style={{ marginLeft: 10, color: '#4f6690', fontSize: 14 }}>Loading menu data…</span>
          </div>
        ) : error ? (
          <div className="mo-body" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, minHeight: 200, justifyContent: 'center' }}>
            <AlertCircle size={28} color="#ef4444" />
            <span style={{ color: '#ef4444', fontSize: 14 }}>{error}</span>
            <button type="button" onClick={loadData} style={{
              padding: '8px 20px', background: '#020617', color: '#fff',
              border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 700
            }}>Retry</button>
          </div>
        ) : (
          <>
            <div className="mo-toggle-band">
              <div className="mo-toggle">
                <button
                  type="button"
                  className={view === 'items' ? 'is-active' : ''}
                  onClick={() => setView('items')}
                >
                  View Individual Items
                </button>
                <button
                  type="button"
                  className={view === 'subcategories' ? 'is-active' : ''}
                  onClick={() => setView('subcategories')}
                >
                  View Sub-Categories
                </button>
              </div>
            </div>

            <div className="mo-body" onDragOver={handleDragOverBody}>
              <section className={`mo-panel ${view === 'items' ? 'mo-items-panel' : 'mo-unmapped-panel'}`}>
                {view === 'subcategories' ? (
                  <>
                    <p className="mo-panel-title">UNMAPPED SUB-CATEGORIES ({unmappedSubcats.length})</p>
                    <p className="mo-panel-help">Drag tokens onto a master cuisine -&gt;</p>
                    <SubCategoryTokens
                      subcats={unmappedSubcats}
                      getItemCount={getItemCount}
                      dragSubCatId={dragSubCatId}
                      onDragStart={setDragSubCatId}
                      onDragEnd={() => setDragSubCatId(null)}
                      draggable
                    />
                  </>
                ) : (
                  <>
                    <p className="mo-panel-title">INDIVIDUAL ITEMS ({filteredItems.length})</p>
                    <div className="mo-search-bar-wrap">
                      <div className="mo-search-input-wrap">
                        <svg className="mo-search-icon" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                        <input
                          className="mo-search"
                          value={search}
                          onChange={(e) => setSearch(e.target.value)}
                          placeholder={searchMode === 'category' ? 'Search by category...' : 'Search items...'}
                        />
                      </div>
                      <div className="mo-search-mode-wrap" ref={searchDropdownRef}>
                        <button
                          type="button"
                          className="mo-search-mode-btn"
                          onClick={() => setSearchDropdownOpen(o => !o)}
                        >
                          <span>{searchMode === 'category' ? 'Category' : 'Item'}</span>
                          <ChevronDown size={14} className={searchDropdownOpen ? 'mo-chevron-open' : ''} />
                        </button>
                        {searchDropdownOpen && (
                          <div className="mo-search-mode-dropdown">
                            {['item', 'category'].map(mode => (
                              <div
                                key={mode}
                                className={`mo-search-mode-option${searchMode === mode ? ' is-active' : ''}`}
                                onClick={() => { setSearchMode(mode); setSearch(''); setSearchDropdownOpen(false); }}
                              >
                                {mode === 'item' ? 'Item' : 'Category'}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="mo-items-scroll">
                      {filteredItems.map((item) => {
                        const subCategory = getSubCategory(item.subCategoryId);
                        const selected = selectedItemId === item.id;
                        return (
                          <div
                            key={item.id}
                            draggable
                            onDragStart={() => setDragItemId(item.id)}
                            onDragEnd={() => setDragItemId(null)}
                            className={`mo-item ${selected ? 'is-selected' : ''} ${
                              dragItemId === item.id ? 'is-dragging' : ''
                            }`}
                          >
                            <button
                              type="button"
                              className="mo-item-main"
                              onClick={() => setSelectedItemId(selected ? null : item.id)}
                            >
                              <span>
                                <GripVertical size={14} strokeWidth={1.8} />
                                {item.name}
                              </span>
                              <em>{subCategory?.name || '-'}</em>
                            </button>
                            {selected && (
                              <div className="mo-reassign">
                                <p>REASSIGN SUB-CATEGORY</p>
                                <div>
                                  {subcats.map((sc) => (
                                    <button
                                      key={sc.id}
                                      type="button"
                                      className={sc.id === item.subCategoryId ? 'is-active' : ''}
                                      onClick={() => reassignItem(item.id, sc.id)}
                                    >
                                      {sc.id === item.subCategoryId && <Check size={12} />}
                                      {sc.name}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </section>

              {view === 'items' ? (
                <section className="mo-panel mo-unmapped-panel mo-reassign-panel">
                  <p className="mo-panel-title">UNMAPPED SUB-CATEGORIES ({unmappedSubcats.length})</p>
                  <SubCategoryTokens
                    subcats={unmappedSubcats}
                    getItemCount={getItemCount}
                    dragOverSubCatId={dragOverSubCatId}
                    onDragOver={(event, subcatId) => {
                      event.preventDefault();
                      setDragOverSubCatId(subcatId);
                    }}
                    onDragLeave={() => setDragOverSubCatId(null)}
                    onDrop={(subcatId) => {
                      if (dragItemId) reassignItem(dragItemId, subcatId);
                      setDragItemId(null);
                      setDragOverSubCatId(null);
                    }}
                  />
                </section>
              ) : (
                <section className="mo-master-wrap">
                  <p className="mo-master-title">MASTER CUISINE STATIONS ({masters.length})</p>
                  {masters.map((master) => {
                    const assigned = master.subCategoryIds
                      .map((id) => getSubCategory(id))
                      .filter(Boolean);
                    const isOver = dragOverMasterId === master.id;

                    return (
                      <div
                        key={master.id}
                        className={`mo-master-card ${isOver ? 'is-over' : ''}`}
                        onDragOver={(event) => {
                          event.preventDefault();
                          setDragOverMasterId(master.id);
                        }}
                        onDragLeave={() => setDragOverMasterId(null)}
                        onDrop={() => handleDropOnMaster(master.id)}
                      >
                        <div className="mo-master-top">
                          <div>
                            <h3>{master.name.trim()}</h3>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <span>Drop zone</span>
                            <button
                              type="button"
                              className="mo-delete-master-btn"
                              onClick={() => deleteMasterCuisine(master.id, master.name)}
                              title={`Delete ${master.name}`}
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </div>
                        {assigned.length > 0 && (
                          <div className="mo-assigned-list">
                            {assigned.map((sc) => (
                              <span key={sc.id}>
                                {sc.name}
                                <button
                                  type="button"
                                  onClick={() => removeFromMaster(master.id, sc.id)}
                                  aria-label={`Remove ${sc.name}`}
                                >
                                  <X size={12} />
                                </button>
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {/* Add Custom Cuisine */}
                  {showAddInput ? (
                    <div className="mo-add-cuisine-input">
                      <input
                        type="text"
                        value={newCuisineName}
                        onChange={(e) => setNewCuisineName(e.target.value)}
                        placeholder="e.g. Tandoori Station"
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') addCustomCuisine();
                          if (e.key === 'Escape') { setShowAddInput(false); setNewCuisineName(''); }
                        }}
                      />
                      <button type="button" onClick={addCustomCuisine} disabled={!newCuisineName.trim()}>
                        Add
                      </button>
                      <button type="button" onClick={() => { setShowAddInput(false); setNewCuisineName(''); }} className="mo-cancel-btn">
                        <X size={14} />
                      </button>
                    </div>
                  ) : (
                    <button type="button" className="mo-add-cuisine" onClick={() => setShowAddInput(true)}>
                      <Plus size={16} />
                      Add Custom Cuisine
                    </button>
                  )}
                </section>
              )}
            </div>

            <div className="mo-footer">
              <p>
                {totalMapped} sub-categor{totalMapped === 1 ? 'y' : 'ies'} mapped · {unmappedSubcats.length} remaining
              </p>
              <div>
                <button type="button" className="mo-skip" onClick={onClose}>
                  Skip
                </button>
                <button
                  type="button"
                  className="mo-finalize"
                  disabled={!canFinalize || saving}
                  onClick={completeMapping}
                >
                  {saving ? (
                    <>
                      <Loader2 size={16} className="mo-spinner" />
                      Saving…
                    </>
                  ) : (
                    <>
                      Confirm
                    </>
                  )}
                </button>
              </div>
            </div>
          </>
        )}

        {/* Toast */}
        {toast && (
          <div className="mo-toast">{toast}</div>
        )}
      </div>
    </div>
  );
};

/* ── Sub-component: SubCategoryTokens ──────────────────────── */

const SubCategoryTokens = ({
  subcats,
  getItemCount,
  dragSubCatId,
  dragOverSubCatId,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDragLeave,
  onDrop,
  draggable = false,
}) => (
  <div className="mo-token-list">
    {subcats.length === 0 && (
      <div className="mo-empty-state">All sub-categories mapped</div>
    )}
    {subcats.map((subcat) => (
      <div
        key={subcat.id}
        draggable={draggable}
        onDragStart={() => onDragStart?.(subcat.id)}
        onDragEnd={onDragEnd}
        onDragOver={(event) => onDragOver?.(event, subcat.id)}
        onDragLeave={onDragLeave}
        onDrop={() => onDrop?.(subcat.id)}
        className={`mo-token ${dragSubCatId === subcat.id ? 'is-dragging' : ''} ${
          dragOverSubCatId === subcat.id ? 'is-over' : ''
        } ${draggable ? 'is-draggable' : ''}`}
      >
        {draggable && <GripVertical size={14} strokeWidth={1.8} />}
        <span>{subcat.name}</span>
        <em>{getItemCount(subcat.id)}</em>
      </div>
    ))}
  </div>
);

export default MenuOrganization;
